import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useLenis } from "lenis/react";
import { Menu, X } from "lucide-react";

const links = [
  { label: "Manifesto", href: "#manifesto" },
  { label: "Services", href: "#services" },
  { label: "Faces", href: "#showcase" },
  { label: "Voices", href: "#testimonials" },
];

export default function Nav({ onApply }) {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const lenis = useLenis();
  const go = (event, href) => {
    event.preventDefault();
    setOpen(false);
    const el = document.querySelector(href);
    if (!el) return;
    window.history.pushState(null, "", href);
    const immediate = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    if (lenis) lenis.scrollTo(el, { offset: -88, immediate, force: true });
    else window.scrollTo({ top: Math.max(0, window.scrollY + el.getBoundingClientRect().top - 88), behavior: "instant" });
  };

  return (
    <motion.header
      data-testid="main-nav"
      initial={{ y: -80 }}
      animate={{ y: 0 }}
      transition={{ duration: 0.8, ease: [0.85, 0, 0.15, 1], delay: 0.2 }}
      className={`fixed top-0 left-0 right-0 z-50 transition-colors duration-500 ${
        scrolled ? "bg-black/70 backdrop-blur-xl border-b border-white/10" : "bg-transparent"
      }`}
    >
      <div className="max-w-[1600px] mx-auto flex items-center justify-between px-6 md:px-10 h-20">
        <a
          href="#top"
          data-testid="nav-logo"
          onClick={(e) => go(e, "#top")}
          className="font-display text-xl md:text-2xl font-black tracking-tighter text-[#ffffff] uppercase"
        >
          Vogue<span className="text-[#ffffff]">Verve</span>
        </a>

        <nav className="hidden md:flex items-center gap-10">
          {links.map((l) => (
            <a
              key={l.href}
              href={l.href}
              data-testid={`nav-link-${l.label.toLowerCase()}`}
              onClick={(e) => go(e, l.href)}
              className="text-xs uppercase tracking-[0.2em] font-bold text-[#ffffff]/70 hover:text-[#ffffff] transition-colors duration-300"
            >
              {l.label}
            </a>
          ))}
        </nav>

        <div className="flex items-center gap-4">
          <button
            data-testid="nav-apply-btn"
            onClick={onApply}
            className="hidden sm:inline-flex items-center rounded-full bg-[#000000] hover:bg-[#1f1f1f] text-white text-xs uppercase tracking-[0.2em] font-bold px-6 py-3 transition-colors duration-300"
          >
            Get Scouted
          </button>
          <button
            data-testid="nav-menu-toggle"
            className="md:hidden text-[#ffffff]"
            onClick={() => setOpen((o) => !o)}
            aria-label={open ? "Close menu" : "Open menu"}
            aria-expanded={open}
            aria-controls="mobile-navigation"
          >
            {open ? <X size={24} strokeWidth={1.5} /> : <Menu size={24} strokeWidth={1.5} />}
          </button>
        </div>
      </div>

      <AnimatePresence>
        {open && (
          <motion.div
            id="mobile-navigation"
            data-testid="mobile-navigation"
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.4, ease: "easeInOut" }}
            className="md:hidden overflow-hidden bg-black/95 backdrop-blur-xl border-b border-white/10"
          >
            <div className="flex flex-col px-6 py-6 gap-5">
              {links.map((l) => (
                <a
                  key={l.href}
                  href={l.href}
                  data-testid={`nav-mobile-link-${l.label.toLowerCase()}`}
                  onClick={(e) => go(e, l.href)}
                  className="text-left font-display text-2xl text-[#ffffff]"
                >
                  {l.label}
                </a>
              ))}
              <button
                data-testid="nav-mobile-apply-btn"
                onClick={() => { setOpen(false); onApply(); }}
                className="mt-2 rounded-full bg-[#000000] text-white text-xs uppercase tracking-[0.2em] font-bold px-6 py-3"
              >
                Get Scouted
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.header>
  );
}
